import { unzip } from "unzipit";
import JSZip from "jszip";
import { messages } from "@/messages";
import { ALLOWED_VIDEO_TYPES, ALLOWED_IMAGES_TYPES } from "@/constants";
import { getVideoDuration } from "@/utils/helpers";
import getDimensionsFromImageDataUrl from "@/utils/getDimensionsFromImageDataUrl";
import { displayAdSizes } from "@/modules/Asset/Creative/CreateOrEditAsset/data";
import { Channel } from "@/types";
import { VALID_ASSET_FILENAME } from "@/constraints";

export const createBulkLinkedinAssets = async (
  file: any,
  advertiserId: any,
  zipFile_CB: any
) => {
  const assetsPromiseList = [];
  const assetNames = [];
  const { entries } = await unzip(file);
  for (const [name, entry] of Object.entries(entries)) {
    if (isValidEntry(name)) {
      const formData = new FormData();
      const mimeType = getFiletype(name);
      formData.append("image", await entry.blob(mimeType));
      formData.append("advertiserId", advertiserId);
      formData.append("isVideo", JSON.stringify(isZipEntryVideo(name)));
      formData.append("fileSize", JSON.stringify(entry.size));
      assetNames.push(getFileName(name));
      assetsPromiseList.push(zipFile_CB({ data: formData, type: "linkedin" }));
    }
  }
  return {
    assetsPromiseList: await Promise.all(assetsPromiseList),
    assetNames,
  };
};

const isValidEntry = (name: string) =>
  isNonHiddenJunkEntry(name) &&
  (isZipEntryVideo(name) || isZipEntryImage(name));
const isNonHiddenJunkEntry = (name: string) => {
  return !name.startsWith("__MACOSX") && name.split(".").length == 2;
};
const getFiletype = (name: string) => {
  return isZipEntryVideo(name)
    ? `video/${getFilExtension(name)}`
    : `image/${getFilExtension(name)}`;
};
const createImageMimeType = (extension: string) => {
  return `image/${extension}`;
};
const getDisplayAdSizeFromDimension = (dimensions: {
  width: number;
  height: number;
}) => {
  return `${dimensions.width}x${dimensions.height}`;
};
const isZipEntryVideo = (name: string) => {
  const splitedArray = name.split(".");
  return ALLOWED_VIDEO_TYPES.includes(splitedArray[splitedArray.length - 1]);
};
const isZipEntryImage = (name: string) => {
  const splitedArray = name.split(".");
  return ALLOWED_IMAGES_TYPES.includes(splitedArray[splitedArray.length - 1]);
};

const isValidFilename = (name: string) => {
  return VALID_ASSET_FILENAME.test(name);
};
const isValidExtension = (ext: string) => {
  return ALLOWED_IMAGES_TYPES.includes(ext);
};

const getFilExtension = (name: string) => {
  const splittedArray = name.split(".");
  return splittedArray[splittedArray.length - 1];
};
export const getZipEntries = async (file: File) => {
  const { entries: allEntries } = await unzip(file);
  const entries = Object.entries(allEntries).filter(
    ([_, entry]) => !entry.isDirectory && entry.name
  );
  const names = entries.map(([name]) => {
    const splittedArray = name.split("/");
    return splittedArray[splittedArray.length - 1];
  });
  const values = entries.map(([_, entry]) => entry);
  const extensions = names.map(getFilExtension);

  return {
    entries,
    names,
    values,
    extensions,
  };
};
export type ZipEntryAssetInfo = {
  dimensions: {
    width: number;
    height: number;
  };
  name: string;
  mimeType: string;
  fileSize: number;
};
type ValidateAssetAndInfoResponse = {
  isError: boolean;
  message: string;
  assetsInfo: ZipEntryAssetInfo[] | undefined;
};

// New types for categorized validation
// Only include errors we can't auto-fix (hidden files are silently ignored, filenames are auto-cleaned)
export enum ZipEntryErrorReason {
  INVALID_EXTENSION = "INVALID_EXTENSION",
  INVALID_DIMENSIONS = "INVALID_DIMENSIONS",
}

export type InvalidZipEntry = {
  name: string;
  reason: ZipEntryErrorReason;
  details?: string;
};

export type ValidateAndCategorizeResponse = {
  validAssets: ZipEntryAssetInfo[];
  invalidAssets: InvalidZipEntry[];
  hasValidAssets: boolean;
  validEntries: Array<{ name: string; blob: Blob }>;
};
export const validateAndGetAssetInfoForDisplay = async (file: File) => {
  const response: ValidateAssetAndInfoResponse = {
    isError: false,
    message: "",
    assetsInfo: undefined,
  };
  const { names, values, extensions } = await getZipEntries(file);

  const isExtensionsValid = extensions.some(isValidExtension);
  if (!isExtensionsValid) {
    return {
      ...response,
      isError: true,
      message: messages.invalidZipEntryType,
    };
  }

  const isNamesValid = names.some((name) => {
    if (!isNonHiddenJunkEntry(name)) {
      return false;
    }
    if (!isValidFilename(name.split(".")[0])) {
      return false;
    }
    return true;
  });

  if (!isNamesValid) {
    return {
      ...response,
      isError: true,
      message: messages.invalidZipEntryName,
    };
  }
  const blobs = await Promise.all(
    values.map((e, i) => e.blob(createImageMimeType(extensions[i])))
  );
  const urls = blobs.map((blob) => window.URL.createObjectURL(blob));
  const dimensions = await Promise.all(
    urls.map((url, i) => {
      // checking if entry is image
      // if not then return default dimensions
      if (!isValidExtension(extensions[i])) return { width: 0, height: 0 };
      return getDimensionsFromImageDataUrl(url)
        .then((dimensions) => {
          window.URL.revokeObjectURL(url);
          return dimensions;
        })
        .catch(() => {
          window.URL.revokeObjectURL(url);
          return { width: 0, height: 0 };
        });
    })
  );
  const validateDisplayAssetSize = (size: string) => {
    return displayAdSizes.includes(size);
  };
  if (
    !dimensions.some((dimension) =>
      validateDisplayAssetSize(getDisplayAdSizeFromDimension(dimension))
    )
  ) {
    return {
      ...response,
      isError: true,
      message: messages.invalidZipImageFileDimensions,
    };
  }

  const assetsInfo = values.map((value, i) => {
    return {
      name: names[i],
      fileSize: value.size,
      mimeType: createImageMimeType(extensions[i]),
      dimensions: dimensions[i],
    };
  });

  return {
    ...response,
    isError: false,
    assetsInfo,
  };
};

export const validateAssetFunctionByChannel = (channel: Channel) => {
  if (channel === "display") {
    return validateAndGetAssetInfoForDisplay;
  }
  if (channel === "linkedin") return validateZipEntriesForLinkedin;
  return null;
};

export const validateZipEntriesForLinkedin = async (
  file: any
): Promise<ValidateAssetAndInfoResponse> => {
  const response: ValidateAssetAndInfoResponse = {
    isError: false,
    message: "",
    assetsInfo: undefined,
  };
  const { entries } = await unzip(file);
  for (const [name, entry] of Object.entries(entries)) {
    if (isValidEntry(name)) {
      if (!isZipEntryImage(name) && !isZipEntryVideo(name))
        return {
          ...response,
          isError: true,
          message: messages.invalidZipEntryType,
          assetsInfo: undefined,
        };
    }
  }
  return {
    ...response,
    isError: false,
  };
};

export const getFileName = (name: string) => {
  const splitedArray = name.split("/");
  return splitedArray[splitedArray.length - 1];
};

export const getAssetInfo = async (
  key: string,
  file: any,
  fileName: string
): Promise<any> => {
  const assetInfo = {};
  const { entries } = await unzip(file);
  for (const [name, entry] of Object.entries(entries)) {
    if (isValidEntry(name)) {
      const mimeType = getFiletype(name);
      const fileBlob = await entry.blob(mimeType);

      const dimentions = isZipEntryImage(name)
        ? await getDimensionsFromImageDataUrl(
            window.URL.createObjectURL(fileBlob)
          )
        : { width: 0, height: 0 };
      return {
        dimentions,
        fileSize: entry.size,
        fileName: cleanFileName(getFileName(name)),
        duration: isZipEntryVideo(name)
          ? await getVideoDuration(fileBlob)
          : undefined,
      };
    }
  }
  return assetInfo;
};

export function cleanFileName(fileName: string) {
  fileName = fileName.trim();
  // Remove unwanted characters (keep a-z, A-Z, 0-9, ', ", -, _)
  fileName = fileName.replace(/[^a-zA-Z0-9'"-_ ]+/g, "");

  // Remove spaces before/after "-" and "_"
  fileName = fileName.replace(/\s*([-_])\s*/g, "$1");

  // Remove leading/trailing "-" or "_"
  fileName = fileName.replace(/^[-_]+|[-_]+$/g, "");

  return fileName;
}

/**
 * Validates ZIP file and categorizes entries into valid/invalid arrays
 * Instead of failing on first invalid file, processes all files and returns categorized results
 *
 * Behavior:
 * - Hidden/junk files (__MACOSX, etc.) → silently ignored
 * - Invalid filenames → auto-cleaned using cleanFileName()
 * - Invalid extension → added to invalidAssets with error
 * - Invalid dimensions → added to invalidAssets with error
 */
export const validateAndCategorizeAssetsForDisplay = async (
  file: File
): Promise<ValidateAndCategorizeResponse> => {
  const validAssets: ZipEntryAssetInfo[] = [];
  const invalidAssets: InvalidZipEntry[] = [];
  const validEntries: Array<{ name: string; blob: Blob }> = [];

  const { entries: allEntries } = await unzip(file);

  for (const [fullPath, entry] of Object.entries(allEntries)) {
    // Skip directories
    if (entry.isDirectory) continue;

    // Get just the filename from path
    const originalFileName = getFileName(fullPath);
    if (!originalFileName) continue;

    // Check 1: Hidden/junk file (starts with __MACOSX or has multiple dots)
    // Silently skip - these are hidden to the user anyway
    if (!isNonHiddenJunkEntry(originalFileName)) {
      continue;
    }

    const extension = getFilExtension(originalFileName);
    const baseName = originalFileName.split(".")[0];

    // Check 2: Auto-clean invalid filenames instead of rejecting
    // Use cleanFileName() to fix names with spaces, special chars, etc.
    const cleanedBaseName = cleanFileName(baseName);
    if (!cleanedBaseName) {
      // Can't salvage this filename, silently skip
      continue;
    }
    const fileName = `${cleanedBaseName}.${extension}`;

    // Check 3: Valid extension (png, jpg, jpeg, gif)
    if (!isValidExtension(extension)) {
      invalidAssets.push({
        name: originalFileName,
        reason: ZipEntryErrorReason.INVALID_EXTENSION,
        details: `Extension ".${extension}" not allowed. Use: png, jpg, jpeg, gif`,
      });
      continue;
    }

    // Check 4: Valid dimensions (must be in displayAdSizes list)
    const mimeType = createImageMimeType(extension);
    const blob = await entry.blob(mimeType);
    const url = window.URL.createObjectURL(blob);

    try {
      const dimensions = await getDimensionsFromImageDataUrl(url);
      window.URL.revokeObjectURL(url);

      const sizeString = getDisplayAdSizeFromDimension(dimensions);

      if (!displayAdSizes.includes(sizeString)) {
        invalidAssets.push({
          name: originalFileName,
          reason: ZipEntryErrorReason.INVALID_DIMENSIONS,
          details: `${sizeString} is not a valid display ad size`,
        });
        continue;
      }

      // All checks passed - file is valid
      // Use cleaned filename for the output
      validAssets.push({
        name: fileName,
        dimensions,
        mimeType,
        fileSize: entry.size,
      });

      validEntries.push({
        name: fileName,
        blob,
      });
    } catch {
      window.URL.revokeObjectURL(url);
      invalidAssets.push({
        name: originalFileName,
        reason: ZipEntryErrorReason.INVALID_DIMENSIONS,
        details: "Could not read image dimensions",
      });
    }
  }

  return {
    validAssets,
    invalidAssets,
    hasValidAssets: validAssets.length > 0,
    validEntries,
  };
};

/**
 * Creates a new ZIP file containing only the valid entries
 * Uses JSZip for browser-compatible ZIP creation
 */
export const createCleanZipFromValidEntries = async (
  validEntries: Array<{ name: string; blob: Blob }>,
  originalFileName: string
): Promise<File> => {
  const zip = new JSZip();

  // Add each valid file to the new ZIP
  for (const { name, blob } of validEntries) {
    zip.file(name, blob);
  }

  // Generate the ZIP as a Blob (no compression needed - backend re-compresses)
  const zipBlob = await zip.generateAsync({ type: "blob" });
  return new File([zipBlob], originalFileName, { type: "application/zip" });
};
